import { Badge, Group, Loader, Select, Stack, Drawer, Text, Divider, Card, Button, Title, Timeline, ActionIcon, Modal, SimpleGrid, TextInput, Textarea, Checkbox, Center, Alert, Code, Tooltip } from "@mantine/core";
import { IconReceipt, IconClock, IconCheck, IconX, IconPlus, IconMinus, IconTrash, IconAlertTriangle, IconSearch, IconPrinter } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useResource, useAction, useCancelOrder, useInfiniteResource,
  useCategories, useProducts, useModifiers, useTables,
  useCreateOrder, useAddOrderLine, useConfirmOrder, useRemoveOrderLine,
  useWaiterOptions, useTableDetail,
} from "../../../shared/api";
import {
  MoneyDisplay, PageHeader, StatusBadge, formatDateTime, EmptyState, InfiniteTable,
  ShortageDialog, type Shortage, validatePhoneTR, PaymentSheet,
} from "../../../shared/ui";
import { useWebSocket } from "../../../shared/ws";
import { LineCompExchangeActions } from "../components/LineCompExchangeActions";

export function OrdersPage() {
  const [status, setStatus] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [orderNo, setOrderNo] = useState("");
  const [debouncedOrderNo] = useDebouncedValue(orderNo.trim(), 300);
  const [sessionNo, setSessionNo] = useState("");
  const [debouncedSessionNo] = useDebouncedValue(sessionNo.trim(), 300);
  const [openId, setOpenId] = useState<string | null>(null);

  // Başka sayfadan navigasyonla gelen parametreler:
  //   open_id: sipariş drawer'ını direkt aç
  //   search: order_no aramasına ön-doldur (Stok Operasyonları
  //           Sipariş referansından gelirken kullanılır)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin:nav:orders");
      if (!raw) return;
      const params = JSON.parse(raw);
      sessionStorage.removeItem("admin:nav:orders");
      if (typeof params.open_id === "string") setOpenId(params.open_id);
      if (typeof params.search === "string") setOrderNo(params.search);
    } catch { /* ignore */ }
  }, []);
  const filters: Record<string, any> = {};
  if (status) filters.kitchen_status = status;
  if (source) filters.source = source;
  if (debouncedOrderNo) filters.order_no = debouncedOrderNo;
  if (debouncedSessionNo) filters.session_no = debouncedSessionNo;
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("orders", { filters });

  // Garson kanalı (/ws/waiters/) bekleyen QR onayları, sipariş onaylama,
  // ödeme tamamlanma event'lerini push'lar. Admin Orders bunları dinleyip
  // listeyi yeniler — F5 gerekmesin diye.
  const qc = useQueryClient();
  const { lastMessage: wsMsg } = useWebSocket<any>("/ws/waiters/");
  useEffect(() => {
    if (!wsMsg) return;
    const event = wsMsg?.event;
    if (!event) return;
    if (
      event === "order.confirmed" ||
      event === "order.cancelled" ||
      event === "order.status_changed" ||
      event === "qr.order.pending_approval" ||
      event === "payment.completed" ||
      // KDS chef "Servis Edildi" basınca → liste durumu PREPARING/READY'den
      // SERVED'a güncellenecek. Önceden bu event sadece istasyon grubuna
      // gidiyordu, admin yakalamıyordu (refresh gerekiyordu); ws_bridge
      // şimdi waiters grubuna da yayınlıyor.
      event === "kds.ticket.served" ||
      event === "kds.ticket.ready"
    ) {
      qc.invalidateQueries({ queryKey: ["orders"] });
    }
  }, [wsMsg, qc]);

  // Sayaçlar yüklenmiş sayfalar üzerinden hesaplanır (infinite scroll —
  // tamamı yüklenince doğru olur, kısmî yükte yaklaşık).
  const activeCount = list.filter((o: any) => o.kitchen_status !== "CANCELLED").length;
  const cancelledCount = list.length - activeCount;
  const subtitle = cancelledCount > 0
    ? `${activeCount} aktif sipariş · ${cancelledCount} iptal`
    : `${activeCount} sipariş`;
  const [newOpen, setNewOpen] = useState(false);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <PageHeader title="Siparişler" subtitle={subtitle} />
        <Button leftSection={<IconPlus size={16} />} onClick={() => setNewOpen(true)}>
          Yeni Sipariş
        </Button>
      </Group>
      {/* Filtreler: mobilde 2'şerli (Adisyon+Sipariş / Durum+Kaynak),
          masaüstünde 4'ü yan yana. Tablo başlık sırasıyla tutarlı:
          adisyon birincil birim, sipariş ikincil. */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <TextInput
          placeholder="Adisyon No ara"
          leftSection={<IconSearch size={16} />}
          value={sessionNo}
          onChange={(e) => setSessionNo(e.currentTarget.value)}
        />
        <TextInput
          placeholder="Sipariş No ara"
          leftSection={<IconSearch size={16} />}
          value={orderNo}
          onChange={(e) => setOrderNo(e.currentTarget.value)}
        />
        <Select
          placeholder="Durum filtresi"
          data={[
            { value: "DRAFT", label: "Taslak" },
            { value: "CONFIRMED", label: "Onaylı" },
            { value: "PREPARING", label: "Hazırlanıyor" },
            { value: "READY", label: "Hazır" },
            { value: "SERVED", label: "Servis Edildi" },
            { value: "CLOSED", label: "Ödendi" },
            { value: "CANCELLED", label: "İptal" },
          ]}
          value={status}
          onChange={setStatus}
          clearable
        />
        <Select
          placeholder="Kaynak filtresi"
          data={["POS", "QR", "GETIR", "YEMEKSEPETI", "TRENDYOL", "DELIVERY"].map((v) => ({ value: v, label: v }))}
          value={source}
          onChange={setSource}
          clearable
        />
      </SimpleGrid>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(o) => o.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(o) => setOpenId(o.id)}
          striped
          minWidth={900}
          emptyContent={<EmptyState title="Sipariş yok" />}
          columns={[
            {
              // Adisyon (TableSession) önce gelir — restoranın birincil birimi
              // adisyondur; aynı masada birden fazla sipariş ortak adisyona
              // bağlıdır. Aynı adisyon ID'sini gören 2 satır → "bu iki sipariş
              // aynı müşterinin sırayla verdiği turlar". Sipariş No, adisyon
              // içindeki tur kimliği olarak ikinci sırada.
              header: "Adisyon",
              render: (o) =>
                o.table_session ? (
                  <code style={{ fontSize: 11 }}>
                    #{String(o.table_session).slice(0, 8)}
                  </code>
                ) : (
                  <Text size="xs" c="dimmed">—</Text>
                ),
            },
            { header: "Sipariş No", render: (o) => <code>{o.id.slice(0, 8)}</code> },
            { header: "Masa", render: (o) => o.table_name || o.table || "-" },
            { header: "Kaynak", render: (o) => <Badge variant="light">{o.source}</Badge> },
            { header: "Garson", render: (o) => o.waiter_name || o.waiter_username || "-" },
            {
              header: "Durum",
              render: (o) => {
                const badge = <StatusBadge status={o.kitchen_status} />;
                if (o.kitchen_status === "CANCELLED" && o.cancelled_reason) {
                  return (
                    <Tooltip label={`İptal sebebi: ${o.cancelled_reason}`} multiline maw={260} withArrow>
                      <span style={{ display: "inline-block", cursor: "help" }}>{badge}</span>
                    </Tooltip>
                  );
                }
                return badge;
              },
            },
            { header: "Tutar", render: (o) => <Text fw={700} span><MoneyDisplay minor={o.total_minor} /></Text> },
            { header: "Onay Zamanı", render: (o) => formatDateTime(o.confirmed_at) },
          ]}
        />
      </Card>

      <OrderDetailDrawer
        orderId={openId}
        onClose={() => setOpenId(null)}
        onOpenOrder={(id) => setOpenId(id)}
      />
      <NewOrderDrawer opened={newOpen} onClose={() => setNewOpen(false)} />
    </Stack>
  );
}

export function OrderDetailDrawer({
  orderId,
  onClose,
  onOpenOrder,
}: {
  orderId: string | null;
  onClose: () => void;
  onOpenOrder: (id: string) => void;
}) {
  const { data: order, isLoading } = useResource<any>("orders", { ...(orderId ? { id: orderId } : {}), enabled: !!orderId });
  const cancel = useCancelOrder();
  const confirm = useAction("orders", "confirm");
  // Adisyon (müşteri hesabı) session-bazlı basılır — masadaki tüm siparişleri
  // tek hesapta toplar. Masaya bağlı değilse (paket/QR) order-bazlı fallback.
  const printSessionReceipt = useAction("table-sessions", "print-receipt");
  const printOrderReceipt = useAction("orders", "print-receipt");
  const removeLine = useRemoveOrderLine();
  const [error, setError] = useState<ApiError | null>(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [lineToDelete, setLineToDelete] = useState<any | null>(null);

  const isDraft = order?.kitchen_status === "DRAFT";

  function openCancel() {
    setCancelReason("");
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!order) return;
    setError(null);
    try {
      await cancel.mutateAsync({ orderId: order.id, reason: cancelReason });
      notifications.show({ message: "Sipariş iptal edildi", color: "green" });
      setCancelOpen(false);
      onClose();
    } catch (e: any) {
      setError(parseApiError(e, "Sipariş iptal edilemedi"));
    }
  }

  async function doConfirm() {
    if (!order) return;
    setError(null);
    try {
      await confirm.mutateAsync({ id: order.id });
      notifications.show({ message: "Sipariş onaylandı", color: "green" });
    } catch (e: any) {
      setError(parseApiError(e, "Sipariş onaylanamadı"));
    }
  }

  async function doPrintReceipt() {
    if (!order) return;
    setError(null);
    try {
      // Masaya bağlıysa adisyonun tamamını (tüm siparişler) bas; değilse
      // (paket/QR) tek siparişi bas.
      const res = order.table_session
        ? await printSessionReceipt.mutateAsync({ id: order.table_session })
        : await printOrderReceipt.mutateAsync({ id: order.id });
      const countNote =
        typeof res.order_count === "number" && res.order_count > 1
          ? ` · ${res.order_count} sipariş`
          : "";
      notifications.show({
        message: `Adisyon yazıcıya gönderildi (${res.device_name})${countNote}`,
        color: "green",
      });
    } catch (e: any) {
      setError(parseApiError(e, "Adisyon yazdırılamadı"));
    }
  }

  function askRemoveLine(line: any) {
    setLineToDelete(line);
  }

  async function confirmRemoveLine() {
    if (!order || !lineToDelete) return;
    setError(null);
    try {
      await removeLine.mutateAsync({ orderId: order.id, lineId: lineToDelete.id });
      notifications.show({ message: "Satır silindi", color: "green" });
      setLineToDelete(null);
    } catch (e: any) {
      setError(parseApiError(e, "Satır silinemedi"));
    }
  }

  return (
    <Drawer
      opened={!!orderId}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        order
          ? order.table_session
            ? `Adisyon #${String(order.table_session).slice(0, 8)} · Sipariş ${order.id?.slice(0, 8)}`
            : `Sipariş ${order.id?.slice(0, 8)}`
          : "Yükleniyor..."
      }
    >
      {isLoading || !order ? <Loader /> : (
        <Stack gap="md">
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between">
              <Group>
                <Badge size="lg" variant="light">{order.source}</Badge>
                <StatusBadge status={order.kitchen_status} />
              </Group>
              <MoneyDisplay minor={order.total_minor} fw={700} size="xl" c="blue" />
            </Group>
            <Divider my="sm" />
            <Stack gap={4}>
              <Text size="sm"><b>Masa:</b> {order.table_name || order.table || "-"}</Text>
              <Text size="sm"><b>Garson:</b> {order.waiter_name || order.waiter_username || "-"}</Text>
              <Text size="sm"><b>Müşteri:</b> {order.customer_name || order.customer || "-"}</Text>
              {order.notes && <Text size="sm"><b>Not:</b> {order.notes}</Text>}
            </Stack>
            {order.kitchen_status === "CANCELLED" && (
              <Alert
                color="red"
                variant="light"
                mt="sm"
                icon={<IconAlertTriangle size={16} />}
                title="İptal nedeni"
              >
                {order.cancelled_reason || <i>(sebep girilmemiş)</i>}
              </Alert>
            )}
          </Card>

          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Title order={5}>Satırlar ({order.lines?.length || 0})</Title>
              {isDraft && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setAddLineOpen(true)}
                >
                  Satır Ekle
                </Button>
              )}
            </Group>
            {(order.lines || []).map((l: any, i: number) => (
              <Group key={l.id || i} justify="space-between" py={4} wrap="nowrap">
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text fw={500}>{l.quantity}× {l.product_name}</Text>
                    {l.comp_quantity_approved > 0 && (
                      <Badge size="xs" color="pink" variant="light">
                        🎁 {l.comp_quantity_approved} ikram
                      </Badge>
                    )}
                    {l.returned_quantity_approved > 0 && (
                      <Badge size="xs" color="indigo" variant="light">
                        ↩ {l.returned_quantity_approved} iade
                      </Badge>
                    )}
                    {l.created_at && (
                      <Text size="xs" c="dimmed">
                        · 🕐 {formatDateTime(l.created_at)}
                      </Text>
                    )}
                  </Group>
                  {(l.modifiers || []).length > 0 && (
                    <Text size="xs" c="dimmed">
                      {(l.modifiers || []).map((m: any) => m.name || m).join(", ")}
                    </Text>
                  )}
                  {(l.comp_minor > 0 || l.returned_minor > 0) && (
                    <Text size="xs" c="pink">
                      {l.comp_minor > 0 && `İkram −${(l.comp_minor / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`}
                      {l.comp_minor > 0 && l.returned_minor > 0 && " · "}
                      {l.returned_minor > 0 && `İade −${(l.returned_minor / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`}
                    </Text>
                  )}
                  {l.notes && <Text size="xs" c="dimmed" fs="italic">{l.notes}</Text>}
                </Stack>
                <Group gap="xs" wrap="nowrap">
                  <MoneyDisplay minor={l.line_total_minor || l.unit_price_minor * l.quantity} fw={600} />
                  {l.id && (
                    <LineCompExchangeActions
                      orderId={order.id}
                      line={l}
                      kitchenStatus={order.kitchen_status}
                    />
                  )}
                  {isDraft && l.id && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => askRemoveLine(l)}
                      loading={removeLine.isPending}
                      title="Satırı sil"
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            ))}
            <Divider my="sm" />
            <Group justify="space-between">
              <Text c="dimmed">Ara Toplam</Text>
              <MoneyDisplay minor={order.subtotal_minor || order.total_minor} />
            </Group>
            {order.discount_minor > 0 && (
              <Group justify="space-between">
                <Text c="violet">İndirim</Text>
                <MoneyDisplay minor={-order.discount_minor} />
              </Group>
            )}
            {order.tip_minor > 0 && (
              <Group justify="space-between">
                <Text c="green">Bahşiş</Text>
                <MoneyDisplay minor={order.tip_minor} />
              </Group>
            )}
            <Group justify="space-between" mt="xs">
              <Text fw={700}>Toplam</Text>
              <MoneyDisplay minor={order.total_minor} fw={700} size="lg" />
            </Group>
          </Card>

          {/* Zaman Çizgisi(leri) — Masaya bağlı sipariş ise o masadaki tüm
              açık siparişler için ayrı ayrı timeline render edilir. Aynı
              garsonun farklı saatlerde aldığı paralel siparişlerde tek
              başlık rozeti (örn. "Servis edildi") yanıltıcı olduğu için her
              siparişin yaşam çizgisi ayrı görünmeli. Masaya bağlı değilse
              (paket/QR vb.) sadece bu siparişin timeline'ı gösterilir. */}
          <OrderTimelineSection order={order} onOpenOrder={onOpenOrder} />


          <ErrorAlert error={error} onClose={() => setError(null)} />

          <Group grow>
            {order.kitchen_status === "DRAFT" && (
              <Button leftSection={<IconCheck size={16} />} color="green" onClick={doConfirm} loading={confirm.isPending}>
                Onayla
              </Button>
            )}
            {/* Adisyon: DRAFT hariç her durumda yazdırılabilir (CLOSED dahil — kopya isteyen müşteri için) */}
            {order.kitchen_status !== "DRAFT" && (
              <Button
                leftSection={<IconPrinter size={16} />}
                variant="light"
                color="blue"
                onClick={doPrintReceipt}
                loading={printSessionReceipt.isPending || printOrderReceipt.isPending}
              >
                Adisyon Yazdır
              </Button>
            )}
            {/* Ödeme Al — açık (henüz ödenmemiş) siparişler için. Masaya bağlı
                olması gerekmez; PaymentSheet order-bazlı tender çağırır ve tam
                ödemede sipariş CLOSED olur. Masasız paket/QR siparişler de
                buradan kapatılabilir (admin'de kasa akışı yoktu). */}
            {!["DRAFT", "CANCELLED", "CLOSED"].includes(order.kitchen_status) && (
              <Button leftSection={<IconReceipt size={16} />} color="green" onClick={() => setPayOpen(true)}>
                Ödeme Al
              </Button>
            )}
            {!["CANCELLED", "CLOSED"].includes(order.kitchen_status) && (
              <Button leftSection={<IconX size={16} />} color="red" variant="light" onClick={openCancel} loading={cancel.isPending}>
                İptal Et
              </Button>
            )}
          </Group>
        </Stack>
      )}

      {/* Ödeme sayfası — tam ödemede backend siparişi CLOSED yapar; onPaid'de
          drawer'ı kapatıp listeyi tazeleriz (WS zaten payment.completed dinler). */}
      {order && (
        <PaymentSheet
          opened={payOpen}
          onClose={() => setPayOpen(false)}
          orders={[order]}
          onPaid={() => { setPayOpen(false); onClose(); }}
        />
      )}

      {order && (
        <AddLineModal
          opened={addLineOpen}
          onClose={() => setAddLineOpen(false)}
          orderId={order.id}
        />
      )}

      {/* Sipariş iptal modal'ı — prompt() yerine düzgün Mantine Modal */}
      <Modal
        opened={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Siparişi İptal Et"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Bu siparişi iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </Text>
          {order && (
            <Card withBorder padding="sm" radius="sm">
              <Stack gap={4}>
                <Text size="sm" fw={600}>
                  Sipariş #{order.id.slice(0, 8)}
                </Text>
                <Text size="xs" c="dimmed">
                  {order.table_name ? `🪑 ${order.table_name}` : "—"}
                  {order.total_minor ? ` · ₺${(order.total_minor / 100).toFixed(2)}` : ""}
                </Text>
              </Stack>
            </Card>
          )}
          <Textarea
            label="İptal sebebi (opsiyonel)"
            placeholder="Örn. müşteri vazgeçti"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCancelOpen(false)} disabled={cancel.isPending}>
              Vazgeç
            </Button>
            <Button color="red" onClick={confirmCancel} loading={cancel.isPending}>
              İptal Et
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Satır silme modal'ı — window.confirm() yerine */}
      <Modal
        opened={!!lineToDelete}
        onClose={() => setLineToDelete(null)}
        title="Satırı Sil"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Bu satırı silmek istediğinize emin misiniz?
          </Text>
          {lineToDelete && (
            <Card withBorder padding="sm" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" fw={600}>
                  {lineToDelete.quantity}× {lineToDelete.product_name}
                </Text>
                <Text size="sm" c="dimmed">
                  ₺{((lineToDelete.unit_price_minor || 0) * (lineToDelete.quantity || 1) / 100).toFixed(2)}
                </Text>
              </Group>
            </Card>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setLineToDelete(null)} disabled={removeLine.isPending}>
              Vazgeç
            </Button>
            <Button color="red" onClick={confirmRemoveLine} loading={removeLine.isPending}>
              Sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}

function getTimelineActive(status: string): number {
  const order = ["DRAFT", "CONFIRMED", "PREPARING", "READY", "SERVED", "CLOSED"];
  return order.indexOf(status);
}

/**
 * Tek siparişin yaşam çizgisi — Oluşturuldu → Onaylandı → Hazırlandı →
 * Servis/İptal → Ödendi. İptalde son adım kırmızı.
 * Son adım "Ödendi" — CLOSED durumu ödeme alındığında oluşur; "Kapatıldı"
 * terimi kullanıcılar tarafından "masa kapatıldı" ile karıştırılıyordu.
 */
function SingleOrderTimeline({ order }: { order: any }) {
  return (
    <Timeline active={getTimelineActive(order.kitchen_status)} bulletSize={20} lineWidth={2}>
      <Timeline.Item bullet={<IconReceipt size={12} />} title="Oluşturuldu">
        <Text size="xs" c="dimmed">{formatDateTime(order.created_at)}</Text>
      </Timeline.Item>
      <Timeline.Item bullet={<IconCheck size={12} />} title="Onaylandı">
        <Text size="xs" c="dimmed">{order.confirmed_at ? formatDateTime(order.confirmed_at) : "—"}</Text>
      </Timeline.Item>
      <Timeline.Item bullet={<IconClock size={12} />} title="Hazırlandı">
        <Text size="xs" c="dimmed">{order.ready_at ? formatDateTime(order.ready_at) : "—"}</Text>
      </Timeline.Item>
      {order.kitchen_status === "CANCELLED" ? (
        <Timeline.Item bullet={<IconX size={12} />} color="red" title="İptal Edildi">
          <Text size="xs" c="dimmed">
            {order.cancelled_at ? formatDateTime(order.cancelled_at) : "—"}
          </Text>
          {order.cancelled_reason && (
            <Text size="xs" c="red.7" mt={2}>
              Sebep: {order.cancelled_reason}
            </Text>
          )}
        </Timeline.Item>
      ) : (
        <Timeline.Item bullet={<IconCheck size={12} />} title="Ödendi">
          <Text size="xs" c="dimmed">{order.closed_at ? formatDateTime(order.closed_at) : "—"}</Text>
        </Timeline.Item>
      )}
    </Timeline>
  );
}

/**
 * Sipariş drawer'ında zaman çizgisi + adisyon bağlamı bölümü.
 *  - Sadece açılan siparişin tam timeline'ı gösterilir.
 *  - Masaya bağlı ve adisyonda birden fazla sipariş varsa altta bir adisyon
 *    özeti çıkar: tüm siparişler (mevcut dahil) id/durum/tutar ile listelenir,
 *    adisyon toplamı gösterilir. Açılan sipariş "Bu sipariş" rozetiyle işaretli;
 *    diğerlerine "Aç" ile geçilebilir — geçince referans kaybolmaz.
 *    Eski "iki sipariş eşit ağırlıkta iki ayrı timeline" sunumu "iki ayrı
 *    müşteri/masa" gibi yanıltıcı olduğu için kaldırıldı.
 *  - Masaya bağlı değilse (paket / QR self) sadece kendi timeline'ı görünür.
 */
function OrderTimelineSection({
  order,
  onOpenOrder,
}: {
  order: any;
  onOpenOrder: (id: string) => void;
}) {
  const tableId: string | null = order?.table || null;
  const { data: tableDetail } = useTableDetail(tableId);

  // table-detail sadece AÇIK siparişleri (CLOSED/CANCELLED hariç) döner.
  // Açılan sipariş ödenmiş (CLOSED) olabilir → o zaman listede yoktur.
  // Mevcut siparişi her durumda dahil et ki "Bu sipariş" satırı görünsün
  // ve adisyon toplamı eksik kalmasın.
  const openOrders: any[] = tableDetail?.orders || [];
  const tableOrders: any[] = openOrders.some((o) => o.id === order.id)
    ? openOrders
    : [order, ...openOrders];

  return (
    <Card padding="md" radius="md" withBorder>
      <Title order={5} mb="sm">Zaman Çizgisi</Title>
      <SingleOrderTimeline order={order} />

      {tableOrders.length > 1 && (
        <Card padding="sm" radius="sm" withBorder mt="md" bg="gray.0">
          <Group justify="space-between" mb={6}>
            <Text size="xs" c="dimmed">
              Bu adisyonda {tableOrders.length} sipariş
            </Text>
            <Text size="xs" fw={700} c="blue">
              Adisyon toplamı:{" "}
              <MoneyDisplay
                minor={tableOrders.reduce((s, o) => s + (o.total_minor || 0), 0)}
                fw={700}
                size="sm"
              />
            </Text>
          </Group>
          {/* Mevcut sipariş dahil tüm adisyon siparişleri listelenir. Açılan
              sipariş "Bu sipariş" rozetiyle işaretli, böylece başka siparişe
              geçince geri dönmek için referans kaybolmaz — "Aç"a basınca
              orijinali yitirme hissi ortadan kalkar. */}
          <Stack gap={6}>
            {tableOrders.map((o) => {
              const isCurrent = o.id === order.id;
              return (
                <Group
                  key={o.id}
                  justify="space-between"
                  wrap="nowrap"
                  p={6}
                  style={{
                    borderRadius: 6,
                    background: isCurrent ? "var(--mantine-color-blue-0)" : undefined,
                    border: isCurrent ? "1px solid var(--mantine-color-blue-3)" : "1px solid transparent",
                  }}
                >
                  <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {String(o.id).slice(0, 8)}
                    </Text>
                    <StatusBadge status={o.kitchen_status} />
                    <MoneyDisplay minor={o.total_minor} fw={600} size="sm" />
                  </Group>
                  {isCurrent ? (
                    <Badge size="xs" variant="light" color="blue">Bu sipariş</Badge>
                  ) : (
                    <Button size="xs" variant="subtle" onClick={() => onOpenOrder(o.id)}>
                      Aç
                    </Button>
                  )}
                </Group>
              );
            })}
          </Stack>
        </Card>
      )}
    </Card>
  );
}

type ApiError = {
  title: string;
  detail: string;
  status?: number;
  raw?: unknown;
};

function parseApiError(e: any, fallback = "Beklenmeyen bir hata oluştu"): ApiError {
  const status = e?.response?.status;
  const data = e?.response?.data;
  let detail = fallback;
  if (typeof data === "string") {
    detail = data;
  } else if (data?.detail) {
    detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
  } else if (data && typeof data === "object") {
    const fieldErrors = Object.entries(data)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join("\n");
    if (fieldErrors) detail = fieldErrors;
  } else if (e?.message) {
    detail = e.message;
  }
  let title = "Hata";
  if (status === 400) title = "Geçersiz istek (400)";
  else if (status === 401) title = "Oturum süresi doldu (401)";
  else if (status === 403) title = "Yetkiniz yok (403)";
  else if (status === 404) title = "Bulunamadı (404)";
  else if (status === 423) title = "Kasiyer kapanışta — kilitli (423)";
  else if (status && status >= 500) title = `Sunucu hatası (${status})`;
  else if (e?.code === "ERR_NETWORK" || e?.message?.includes("Network")) {
    title = "Bağlantı hatası";
    detail = "Backend'e ulaşılamıyor. Servis çalışıyor mu kontrol edin.";
  }
  return { title, detail, status, raw: data };
}

function ErrorAlert({ error, onClose }: { error: ApiError | null; onClose?: () => void }) {
  if (!error) return null;
  return (
    <Alert
      icon={<IconAlertTriangle size={18} />}
      title={error.title}
      color="red"
      variant="light"
      withCloseButton={!!onClose}
      {...(onClose ? { onClose } : {})}
    >
      <Stack gap={4}>
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{error.detail}</Text>
        {!!error.raw && typeof error.raw === "object" && Object.keys(error.raw).length > 0 && (
          <details>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--mantine-color-dimmed)" }}>
              Teknik detay
            </summary>
            <Code block style={{ fontSize: 11, marginTop: 4 }}>
              {JSON.stringify(error.raw, null, 2)}
            </Code>
          </details>
        )}
      </Stack>
    </Alert>
  );
}

type CartItem = {
  id: string;
  productId: string;
  name: string;
  price: number;
  qty: number;
  modifier_ids: string[];
  modifier_names: string[];
  notes: string;
  // Stok yetersizliğinde reçeteden çıkarılan opsiyonel malzemeler (POS ile aynı).
  excluded_ingredient_ids?: string[];
  excluded_ingredient_names?: string[];
};

const SOURCE_OPTIONS = [
  { value: "CALL_CENTER", label: "Telefon / Çağrı Merkezi" },
  { value: "POS", label: "POS (Garson)" },
  { value: "GETIR", label: "Getir Yemek" },
  { value: "YEMEKSEPETI", label: "Yemeksepeti" },
  { value: "TRENDYOL", label: "Trendyol Yemek" },
  { value: "KIOSK", label: "Kiosk" },
];

const FULFILLMENT_OPTIONS = [
  { value: "DINE_IN", label: "Masada Servis" },
  { value: "TAKEAWAY", label: "Paket" },
  { value: "DELIVERY", label: "Teslimat" },
];

export function NewOrderDrawer({
  opened,
  onClose,
  initialSource = "CALL_CENTER",
  initialFulfillment = "DELIVERY",
  initialTableId = null,
  initialWaiterId = null,
  lockTable = false,
}: {
  opened: boolean;
  onClose: () => void;
  initialSource?: string;
  initialFulfillment?: string;
  initialTableId?: string | null;
  initialWaiterId?: string | null;
  /** Masa salon planından önceden seçildiyse, masa seçici kilitlensin. */
  lockTable?: boolean;
}) {
  const [source, setSource] = useState<string>(initialSource);
  const [fulfillment, setFulfillment] = useState<string>(initialFulfillment);
  const [tableId, setTableId] = useState<string | null>(initialTableId);
  const [waiterId, setWaiterId] = useState<string | null>(initialWaiterId);

  // Drawer her açıldığında initial prop'ları yansıt — salon planındaki
  // farklı bir masaya geçildiğinde eski tableId/waiterId takılı kalmasın.
  useEffect(() => {
    if (opened) {
      setSource(initialSource);
      setFulfillment(initialFulfillment);
      setTableId(initialTableId);
      setWaiterId(initialWaiterId);
    }
  }, [opened, initialSource, initialFulfillment, initialTableId, initialWaiterId]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [notes, setNotes] = useState("");

  const { data: catsData, isLoading: catsLoading } = useCategories();
  const cats: any[] = useMemo(() => catsData?.results || catsData || [], [catsData]);
  // Kategori ağacı: backend `parent` (DRF FK) döndürür; QR endpoint'i `parent_id`.
  // İkisini de tolere et.
  const parentOf = (c: any) => c.parent ?? c.parent_id ?? null;
  const roots = useMemo(() => cats.filter((c) => !parentOf(c)), [cats]);
  const childrenOf = (rootId: string) => cats.filter((c) => parentOf(c) === rootId);
  const [activeRoot, setActiveRoot] = useState<string | null>(null);
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const childCats = activeRoot ? childrenOf(activeRoot) : [];
  // Filtre: alt kategori seçildiyse onu kullan; yoksa kökün kendisini
  // (köke doğrudan bağlı ürünler için).
  const activeCat = activeChild || (activeRoot && childCats.length === 0 ? activeRoot : null);
  const { data: prodsData, isLoading: prodsLoading } = useProducts(
    activeCat ? { category: activeCat } : undefined,
  );
  const { data: modsData } = useModifiers();
  const { data: tablesData } = useTables();
  const { data: waiterOptions } = useWaiterOptions();
  const modifiers: any[] = modsData?.results || modsData || [];
  // Ürünleri alfabetik göster (Türkçe locale: İ/ş/ç/ğ doğru sıralanır).
  const products: any[] = [...(prodsData?.results || prodsData || [])].sort(
    (a: any, b: any) => (a.name || "").localeCompare(b.name || "", "tr"),
  );
  const tables: any[] = tablesData?.results || tablesData || [];

  const [cart, setCart] = useState<CartItem[]>([]);
  const [modifierProduct, setModifierProduct] = useState<any | null>(null);
  const [selectedMods, setSelectedMods] = useState<string[]>([]);
  const [lineNotes, setLineNotes] = useState("");
  // Stok yetersizliği pop-up'ı — POS TableScreen ile aynı model: backend
  // add_line 400 + shortages döndüğünde açılır, opsiyonel malzemeler çıkarılır.
  const [shortageDialog, setShortageDialog] = useState<{
    cartItemId: string;
    productName: string;
    shortages: Shortage[];
    /** Hiç satır gönderemeden 400 alınırsa yaratılan yetim DRAFT order. */
    orphanOrderId: string | null;
  } | null>(null);
  // Stok pop-up'ı sonrası "tekrar gönder"de aynı order'a devam etmek için
  // bu turda yaratılmış DRAFT order'ın id'si tutulur (yeniden yaratma olmasın).
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);

  const createOrder = useCreateOrder();
  const addLine = useAddOrderLine();
  const confirmOrder = useConfirmOrder();
  const [error, setError] = useState<ApiError | null>(null);
  const [stage, setStage] = useState<"idle" | "creating" | "adding_lines" | "confirming">("idle");

  if (!activeRoot && roots.length > 0) {
    setActiveRoot(roots[0].id);
  }

  function selectRoot(rootId: string) {
    setActiveRoot(rootId);
    setActiveChild(null);
  }

  function resetForm() {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setNotes("");
    setTableId(initialTableId);
    setWaiterId(initialWaiterId);
    setSource(initialSource);
    setFulfillment(initialFulfillment);
    setError(null);
    setStage("idle");
    setShortageDialog(null);
    setPendingOrderId(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  // Ürüne özgü modifier'lar — ProductSerializer her ürüne `modifiers`
  // listesini gömüyor (tenant'ın modifier_filter_mode ayarına göre filtreli).
  const productModifiers = (p: any): any[] => p?.modifiers || [];

  function addProductToCart(p: any, mods: string[] = [], notes = "") {
    const allMods = [...productModifiers(p), ...modifiers];
    const modsTotal = mods.reduce((s, mid) => {
      const m = allMods.find((x: any) => x.id === mid);
      return s + (m?.price_minor || 0);
    }, 0);
    const itemId = `${p.id}|${[...mods].sort().join(",")}|${notes}`;
    setCart((prev) => {
      const ex = prev.find((c) => c.id === itemId);
      if (ex) return prev.map((c) => (c.id === itemId ? { ...c, qty: c.qty + 1 } : c));
      return [...prev, {
        id: itemId,
        productId: p.id,
        name: p.name,
        price: (p.unit_price_minor || p.price_minor || 0) + modsTotal,
        qty: 1,
        modifier_ids: mods,
        modifier_names: mods
          .map((mid) => allMods.find((m: any) => m.id === mid)?.name)
          .filter(Boolean) as string[],
        notes,
      }];
    });
  }

  function handleProductClick(p: any) {
    // Modifier bağlı değilse direkt sepete — modal gereksiz
    if (productModifiers(p).length === 0) {
      addProductToCart(p);
      return;
    }
    setModifierProduct(p);
    setSelectedMods([]);
    setLineNotes("");
  }

  function confirmAddToCart() {
    if (!modifierProduct) return;
    addProductToCart(modifierProduct, selectedMods, lineNotes);
    setModifierProduct(null);
  }

  function changeQty(id: string, delta: number) {
    setCart((prev) =>
      prev
        .map((c) => (c.id === id ? { ...c, qty: c.qty + delta } : c))
        .filter((c) => c.qty > 0),
    );
  }

  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const isDineIn = fulfillment === "DINE_IN";
  const needsCustomer = fulfillment !== "DINE_IN";

  async function handleSubmit() {
    setError(null);
    if (cart.length === 0) {
      setError({ title: "Sepet boş", detail: "En az bir ürün ekleyin." });
      return;
    }
    if (isDineIn && !tableId) {
      setError({ title: "Masa seçilmedi", detail: "Masada servis için masa seçin." });
      return;
    }
    if (isDineIn && !waiterId) {
      setError({ title: "Garson seçilmedi", detail: "Masada servis için ilgili garsonu seçin." });
      return;
    }
    if (needsCustomer && !customerName.trim()) {
      setError({ title: "Müşteri adı zorunlu", detail: "Paket veya teslimat siparişi için müşteri adı girin." });
      return;
    }
    const customerPhoneErr = validatePhoneTR(customerPhone);
    if (customerPhoneErr) {
      setError({ title: "Telefon geçersiz", detail: customerPhoneErr });
      return;
    }
    let createdOrderId: string | null = pendingOrderId;
    try {
      // Order'ı bu turda zaten yarattıysak (stok pop-up sonrası tekrar gönder)
      // aynı DRAFT'a devam et; yoksa yeni yarat.
      let orderId = pendingOrderId;
      if (!orderId) {
        const payload: Record<string, any> = {
          source,
          fulfillment_type: fulfillment,
          customer_name: customerName,
          customer_phone: customerPhone,
          notes,
        };
        if (isDineIn && tableId) payload.table = tableId;
        if (waiterId) payload.waiter = waiterId;

        setStage("creating");
        const order = await createOrder.mutateAsync(payload);
        orderId = order.id;
        createdOrderId = order.id;
        setPendingOrderId(order.id);
      }
      // Bu noktada orderId kesin var (yukarıda yaratıldı ya da pendingOrderId'den).
      if (!orderId) throw new Error("Sipariş kimliği oluşturulamadı.");
      const oid = orderId;

      setStage("adding_lines");
      // POS modeli: stok yetersizliğinde gönderilmiş satırları koru, kalanları
      // (bu dahil) sepette bırak, opsiyonel malzeme pop-up'ı aç.
      const sentIds: string[] = [];
      for (const item of cart) {
        try {
          await addLine.mutateAsync({
            orderId: oid,
            product_id: item.productId,
            quantity: item.qty,
            modifier_ids: item.modifier_ids,
            notes: item.notes,
            ...(item.excluded_ingredient_ids && item.excluded_ingredient_ids.length > 0
              ? { excluded_ingredient_ids: item.excluded_ingredient_ids }
              : {}),
          });
          sentIds.push(item.id);
        } catch (err: any) {
          const shortages: Shortage[] | undefined = err?.response?.data?.shortages;
          if (shortages && shortages.length > 0) {
            setCart((prev) => prev.filter((c) => !sentIds.includes(c.id)));
            setShortageDialog({
              cartItemId: item.id,
              productName: item.name,
              shortages,
              orphanOrderId: sentIds.length === 0 ? oid : null,
            });
            setStage("idle");
            return;
          }
          throw err;
        }
      }

      setStage("confirming");
      await confirmOrder.mutateAsync(oid);

      setStage("idle");
      setPendingOrderId(null);
      notifications.show({ message: "Sipariş oluşturuldu ve mutfağa gönderildi", color: "green" });
      handleClose();
    } catch (e: any) {
      const stageLabel = {
        idle: "",
        creating: "Sipariş oluşturulamadı",
        adding_lines: "Ürün satırı eklenirken hata",
        confirming: "Sipariş onaylanamadı (DRAFT olarak kaldı)",
      }[stage];
      const parsed = parseApiError(e, stageLabel || "Sipariş gönderilemedi");
      if (stageLabel && !parsed.title.includes(stageLabel)) {
        parsed.title = `${stageLabel} — ${parsed.title}`;
      }
      if (stage === "confirming" && createdOrderId) {
        parsed.detail += `\n\nSipariş #${createdOrderId.slice(0, 8)} DRAFT olarak kaydedildi. Sipariş listesinden tekrar onaylayabilirsiniz.`;
      }
      setError(parsed);
      setStage("idle");
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="xl"
      title="Yeni Sipariş"
    >
      <Stack gap="md">
        <Card padding="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group grow>
              <Select
                label="Kaynak"
                data={SOURCE_OPTIONS}
                value={source}
                onChange={(v) => setSource(v || "CALL_CENTER")}
                allowDeselect={false}
              />
              <Select
                label="Tip"
                data={FULFILLMENT_OPTIONS}
                value={fulfillment}
                onChange={(v) => setFulfillment(v || "DELIVERY")}
                allowDeselect={false}
              />
            </Group>
            {isDineIn ? (
              <Group grow align="flex-start">
                <Select
                  label="Masa"
                  placeholder="Masa seçin"
                  data={tables.filter((t) => t.is_active !== false).map((t) => ({ value: t.id, label: t.name }))}
                  value={tableId}
                  onChange={setTableId}
                  searchable
                  clearable={!lockTable}
                  disabled={lockTable}
                  description={lockTable ? "Salon planından seçilen masa" : undefined}
                />
                <Select
                  label="İlgili Garson"
                  placeholder="Garson seçin"
                  data={waiterOptions || []}
                  value={waiterId}
                  onChange={setWaiterId}
                  searchable
                  clearable
                  required
                />
              </Group>
            ) : (
              <>
                <Group grow>
                  <TextInput
                    label="Müşteri Adı"
                    placeholder="Ahmet Yılmaz"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.currentTarget.value)}
                    required
                  />
                  <TextInput
                    label="Telefon"
                    type="tel"
                    inputMode="tel"
                    placeholder="+90 555 123 45 67"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.currentTarget.value)}
                    error={customerPhone ? validatePhoneTR(customerPhone) : null}
                  />
                </Group>
                <Select
                  label="İlgili Garson (opsiyonel)"
                  placeholder="Garson seçin"
                  data={waiterOptions || []}
                  value={waiterId}
                  onChange={setWaiterId}
                  searchable
                  clearable
                />
              </>
            )}
            <Textarea
              label="Sipariş Notu (opsiyonel)"
              placeholder="Adres, teslimat saati, özel istek..."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              autosize
              minRows={2}
            />
          </Stack>
        </Card>

        <Card padding="md" radius="md" withBorder>
          <Title order={6} mb="xs">Ürünler</Title>
          {catsLoading ? (
            <Center h={120}><Loader /></Center>
          ) : cats.length === 0 ? (
            <EmptyState title="Henüz kategori yok" />
          ) : (
            <Stack gap="sm">
              {/* Ana kategori sırası */}
              <Group gap="xs" wrap="wrap">
                {roots.map((r: any) => (
                  <Button
                    key={r.id}
                    variant={activeRoot === r.id ? "filled" : "default"}
                    onClick={() => selectRoot(r.id)}
                    size="sm"
                    style={{ flexShrink: 0 }}
                  >
                    {r.name}
                  </Button>
                ))}
              </Group>
              {/* Alt kategori sırası — sadece kökün altı varsa */}
              {childCats.length > 0 && (
                <Group gap="xs" wrap="wrap" pl="xs" style={{ borderLeft: "2px solid var(--mantine-color-blue-3)" }}>
                  {childCats.map((c: any) => (
                    <Button
                      key={c.id}
                      variant={activeChild === c.id ? "filled" : "light"}
                      color="blue"
                      onClick={() => setActiveChild(c.id)}
                      size="xs"
                      style={{ flexShrink: 0 }}
                    >
                      {c.name}
                    </Button>
                  ))}
                </Group>
              )}
              {childCats.length > 0 && !activeChild ? (
                <Text c="dimmed" ta="center" py="md" size="sm">Bir alt kategori seçin</Text>
              ) : prodsLoading ? (
                <Center h={100}><Loader /></Center>
              ) : products.length === 0 ? (
                <Text c="dimmed" ta="center" py="md" size="sm">Bu kategoride ürün yok</Text>
              ) : (
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                  {products.map((p: any) => (
                    <Card
                      key={p.id}
                      padding="sm"
                      radius="md"
                      withBorder
                      onClick={() => handleProductClick(p)}
                      style={{ cursor: "pointer", height: "100%" }}
                    >
                      <Stack gap={4} h="100%" justify="space-between">
                        <Text fw={600} size="sm" lineClamp={2}>{p.name}</Text>
                        <MoneyDisplay minor={p.unit_price_minor || p.price_minor || 0} fw={700} c="blue" size="sm" />
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Stack>
          )}
        </Card>

        {cart.length > 0 && (
          <Card padding="md" radius="md" withBorder bg="gray.0">
            <Stack gap="xs">
              <Text fw={700}>Sepet</Text>
              {cart.map((c) => (
                <Group key={c.id} justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" truncate>{c.name}</Text>
                    {c.modifier_names.length > 0 && (
                      <Text size="xs" c="yellow.7" truncate>+ {c.modifier_names.join(", ")}</Text>
                    )}
                    {c.notes && <Text size="xs" c="dimmed" fs="italic" truncate>{c.notes}</Text>}
                  </Stack>
                  <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <ActionIcon size="sm" variant="default" onClick={() => changeQty(c.id, -1)}>
                      <IconMinus size={12} />
                    </ActionIcon>
                    <Badge variant="filled" w={28} style={{ textAlign: "center" }}>{c.qty}</Badge>
                    <ActionIcon size="sm" variant="default" onClick={() => changeQty(c.id, +1)}>
                      <IconPlus size={12} />
                    </ActionIcon>
                    <Text size="sm" fw={600} ta="right" style={{ minWidth: 88 }}>
                      <MoneyDisplay minor={c.price * c.qty} fw={600} size="sm" />
                    </Text>
                  </Group>
                </Group>
              ))}
              <Divider />
              <Group justify="space-between">
                <Text fw={700}>Toplam</Text>
                <MoneyDisplay minor={total} fw={700} size="lg" c="blue" />
              </Group>
            </Stack>
          </Card>
        )}

        <ErrorAlert error={error} onClose={() => setError(null)} />

        <Group grow>
          <Button variant="default" leftSection={<IconTrash size={16} />} onClick={() => setCart([])} disabled={cart.length === 0}>
            Sepeti Temizle
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleSubmit}
            loading={createOrder.isPending || addLine.isPending || confirmOrder.isPending}
            disabled={cart.length === 0}
          >
            Onayla ve Gönder
          </Button>
        </Group>
      </Stack>

      <Modal
        opened={!!modifierProduct}
        onClose={() => setModifierProduct(null)}
        title={modifierProduct ? `${modifierProduct.name} — Özelleştir` : ""}
        size="md"
      >
        {modifierProduct && (
          <Stack gap="md">
            <Card padding="sm" radius="md" withBorder bg="gray.0">
              <Group justify="space-between">
                <Text>{modifierProduct.name}</Text>
                <MoneyDisplay minor={modifierProduct.unit_price_minor || modifierProduct.price_minor || 0} fw={700} />
              </Group>
            </Card>
            {productModifiers(modifierProduct).length > 0 && (
              <Stack gap="xs">
                <Text fw={600} size="sm">Modifier'lar (opsiyonel)</Text>
                {productModifiers(modifierProduct)
                  .filter((m: any) => m.is_active !== false)
                  .map((m: any) => (
                    <Checkbox
                      key={m.id}
                      label={
                        <Group justify="space-between" style={{ width: "100%" }}>
                          <Text>{m.name}</Text>
                          {m.price_minor > 0 && <MoneyDisplay minor={m.price_minor} c="dimmed" />}
                        </Group>
                      }
                      checked={selectedMods.includes(m.id)}
                      onChange={(e) => {
                        if (e.currentTarget.checked) setSelectedMods([...selectedMods, m.id]);
                        else setSelectedMods(selectedMods.filter((x) => x !== m.id));
                      }}
                    />
                  ))}
              </Stack>
            )}
            <Textarea
              label="Not (opsiyonel)"
              placeholder="Az pişmiş, soğansız..."
              value={lineNotes}
              onChange={(e) => setLineNotes(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModifierProduct(null)}>İptal</Button>
              <Button onClick={confirmAddToCart}>Sepete Ekle</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Stok yetersizliği — POS ile aynı: opsiyonel malzemeleri çıkar ve devam et */}
      {shortageDialog && (
        <ShortageDialog
          state={shortageDialog}
          onClose={() => setShortageDialog(null)}
          onExcludeAll={() => {
            setCart((prev) => prev.map((c) =>
              c.id === shortageDialog.cartItemId
                ? {
                    ...c,
                    excluded_ingredient_ids: shortageDialog.shortages
                      .filter((s) => s.is_optional)
                      .map((s) => s.ingredient_id),
                    excluded_ingredient_names: shortageDialog.shortages
                      .filter((s) => s.is_optional)
                      .map((s) => s.name),
                  }
                : c,
            ));
            setShortageDialog(null);
            notifications.show({
              color: "blue",
              message: "Eksik malzemeler çıkarıldı, tekrar 'Onayla ve Gönder'e basın.",
            });
          }}
        />
      )}
    </Drawer>
  );
}

function AddLineModal({ opened, onClose, orderId }: { opened: boolean; onClose: () => void; orderId: string }) {
  const { data: catsData } = useCategories();
  const cats: any[] = useMemo(() => catsData?.results || catsData || [], [catsData]);
  const parentOf = (c: any) => c.parent ?? c.parent_id ?? null;
  const roots = useMemo(() => cats.filter((c) => !parentOf(c)), [cats]);
  const childrenOf = (rootId: string) => cats.filter((c) => parentOf(c) === rootId);
  const [activeRoot, setActiveRoot] = useState<string | null>(null);
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const childCats = activeRoot ? childrenOf(activeRoot) : [];
  const activeCat = activeChild || (activeRoot && childCats.length === 0 ? activeRoot : null);
  const { data: prodsData, isLoading: prodsLoading } = useProducts(
    activeCat ? { category: activeCat } : undefined,
  );
  const products: any[] = prodsData?.results || prodsData || [];

  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedMods, setSelectedMods] = useState<string[]>([]);
  const [lineNotes, setLineNotes] = useState("");
  const [error, setError] = useState<ApiError | null>(null);

  const addLine = useAddOrderLine();

  if (opened && !activeRoot && roots.length > 0) {
    setActiveRoot(roots[0].id);
  }

  function selectRoot(rootId: string) {
    setActiveRoot(rootId);
    setActiveChild(null);
  }

  function reset() {
    setSelectedProduct(null);
    setQuantity(1);
    setSelectedMods([]);
    setLineNotes("");
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleAdd() {
    if (!selectedProduct) return;
    setError(null);
    try {
      await addLine.mutateAsync({
        orderId,
        product_id: selectedProduct.id,
        quantity,
        modifier_ids: selectedMods,
        notes: lineNotes,
      });
      notifications.show({ message: `${quantity}× ${selectedProduct.name} eklendi`, color: "green" });
      handleClose();
    } catch (e: any) {
      setError(parseApiError(e, "Satır eklenemedi"));
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Satır Ekle" size="lg">
      <Stack gap="md">
        {!selectedProduct ? (
          <>
            <Group gap="xs" wrap="wrap">
              {roots.map((r: any) => (
                <Button
                  key={r.id}
                  variant={activeRoot === r.id ? "filled" : "default"}
                  onClick={() => selectRoot(r.id)}
                  size="sm"
                  style={{ flexShrink: 0 }}
                >
                  {r.name}
                </Button>
              ))}
            </Group>
            {childCats.length > 0 && (
              <Group gap="xs" wrap="wrap" pl="xs" style={{ borderLeft: "2px solid var(--mantine-color-blue-3)" }}>
                {childCats.map((c: any) => (
                  <Button
                    key={c.id}
                    variant={activeChild === c.id ? "filled" : "light"}
                    color="blue"
                    onClick={() => setActiveChild(c.id)}
                    size="xs"
                    style={{ flexShrink: 0 }}
                  >
                    {c.name}
                  </Button>
                ))}
              </Group>
            )}
            {childCats.length > 0 && !activeChild ? (
              <Text c="dimmed" ta="center" py="md" size="sm">Bir alt kategori seçin</Text>
            ) : prodsLoading ? (
              <Center h={120}><Loader /></Center>
            ) : products.length === 0 ? (
              <Text c="dimmed" ta="center" py="md" size="sm">Bu kategoride ürün yok</Text>
            ) : (
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                {products.map((p: any) => (
                  <Card
                    key={p.id}
                    padding="sm"
                    radius="md"
                    withBorder
                    onClick={() => {
                      setSelectedProduct(p);
                      setSelectedMods([]);
                      setLineNotes("");
                      setQuantity(1);
                    }}
                    style={{ cursor: "pointer", height: "100%" }}
                  >
                    <Stack gap={4} h="100%" justify="space-between">
                      <Text fw={600} size="sm" lineClamp={2}>{p.name}</Text>
                      <MoneyDisplay minor={p.unit_price_minor || p.price_minor || 0} fw={700} c="blue" size="sm" />
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </>
        ) : (
          <>
            <Card padding="sm" radius="md" withBorder bg="gray.0">
              <Group justify="space-between">
                <Text fw={600}>{selectedProduct.name}</Text>
                <MoneyDisplay minor={selectedProduct.unit_price_minor || selectedProduct.price_minor || 0} fw={700} />
              </Group>
            </Card>
            <Group>
              <Text fw={500}>Adet:</Text>
              <ActionIcon variant="default" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                <IconMinus size={14} />
              </ActionIcon>
              <Badge size="lg" variant="filled">{quantity}</Badge>
              <ActionIcon variant="default" onClick={() => setQuantity(quantity + 1)}>
                <IconPlus size={14} />
              </ActionIcon>
            </Group>
            {(selectedProduct.modifiers || []).length > 0 && (
              <Stack gap="xs">
                <Text fw={600} size="sm">Modifier'lar (opsiyonel)</Text>
                {(selectedProduct.modifiers as any[])
                  .filter((m: any) => m.is_active !== false)
                  .map((m: any) => (
                    <Checkbox
                      key={m.id}
                      label={
                        <Group justify="space-between" style={{ width: "100%" }}>
                          <Text>{m.name}</Text>
                          {m.price_minor > 0 && <MoneyDisplay minor={m.price_minor} c="dimmed" />}
                        </Group>
                      }
                      checked={selectedMods.includes(m.id)}
                      onChange={(e) => {
                        if (e.currentTarget.checked) setSelectedMods([...selectedMods, m.id]);
                        else setSelectedMods(selectedMods.filter((x) => x !== m.id));
                      }}
                    />
                  ))}
              </Stack>
            )}
            <Textarea
              label="Not (opsiyonel)"
              placeholder="Az pişmiş, soğansız..."
              value={lineNotes}
              onChange={(e) => setLineNotes(e.currentTarget.value)}
            />
            <ErrorAlert error={error} onClose={() => setError(null)} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setSelectedProduct(null)}>Geri</Button>
              <Button onClick={handleAdd} loading={addLine.isPending} leftSection={<IconCheck size={16} />}>
                Ekle
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
